import type { StyleHotProductCategoryProduct } from '../types';

export type StyleProductImageKind = 'product' | 'product_gray' | 'celebrity' | 'material' | 'other';

export interface StyleProductImageInfo {
  src: string;
  kind: StyleProductImageKind;
}

interface PcStyleProductCardImages {
  productImage: StyleProductImageInfo;
  hoverProductImage: StyleProductImageInfo;
  shouldShowHoverProductImage: boolean;
}

const EMPTY_IMAGE: StyleProductImageInfo = {
  src: '',
  kind: 'other',
};

function getPrimaryImage(product: StyleHotProductCategoryProduct): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;
  const imageType = product.pc_image_type || 'product';
  const candidates: Array<[StyleProductImageKind, string[] | undefined]> = [
    [imageType as StyleProductImageKind, imageMap[imageType]],
    ['celebrity', imageMap.celebrity],
    ['material', imageMap.material],
    ['product', imageMap.product],
    ['product_gray', imageMap.product_gray],
  ];
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) {
    return EMPTY_IMAGE;
  }

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getDefaultHoverImage(product: StyleHotProductCategoryProduct): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;
  const candidates: Array<[StyleProductImageKind, string[] | undefined]> = [
    ['product_gray', imageMap.product_gray],
    ['product', imageMap.product],
  ];
  const matchedImage = candidates.find(([, images]) => images?.[0]);

  if (!matchedImage) {
    return EMPTY_IMAGE;
  }

  const [kind, images] = matchedImage;

  return {
    src: images?.[0] || '',
    kind,
  };
}

function getProductGrayConfiguredHoverImage(
  product: StyleHotProductCategoryProduct,
  productImage: StyleProductImageInfo,
): StyleProductImageInfo {
  const imageMap = (product.image || {}) as Record<string, string[] | undefined>;

  if (productImage.kind === 'product_gray' && imageMap.product_gray?.[1]) {
    return {
      src: imageMap.product_gray[1],
      kind: 'product_gray',
    };
  }

  if (productImage.kind === 'product' && imageMap.product?.[1]) {
    return {
      src: imageMap.product[1],
      kind: 'product',
    };
  }

  return EMPTY_IMAGE;
}

export function getPcStyleProductCardImages(
  product: StyleHotProductCategoryProduct,
): PcStyleProductCardImages {
  const productImage = getPrimaryImage(product);
  const isProductGrayConfigured = product.pc_image_type === 'product_gray';
  const hoverProductImage = isProductGrayConfigured
    ? getProductGrayConfiguredHoverImage(product, productImage)
    : getDefaultHoverImage(product);
  const shouldShowHoverProductImage =
    Boolean(hoverProductImage.src) &&
    (isProductGrayConfigured ||
      productImage.kind === 'celebrity' ||
      productImage.kind === 'material');

  return {
    productImage,
    hoverProductImage,
    shouldShowHoverProductImage,
  };
}
